/*
 * Rebuy "Frequently Bought Together" — keep deselected product images visible.
 *
 * Problem: Rebuy's dynamic-bundle widget renders product images in a separate
 * `.rebuy-bundle__images` strip and REMOVES a product's image from the DOM the
 * moment its checkbox is unchecked (the title survives because it lives in the
 * separate `.rebuy-bundle__items` container).
 *
 * Fix: render our own persistent thumbnail inside each `.rebuy-product-block`
 * (that block is never removed — only its `is-selected` class is toggled), and
 * let CSS dim it to opacity 0.5 when the block is deselected. Because Rebuy
 * mounts asynchronously and re-renders (wiping injected nodes) on every toggle,
 * we cache the image sources once and re-inject via a MutationObserver.
 */
(function () {
  // Image sources keyed by `product-id-XXXX` class, captured from Rebuy's native
  // strip while every product is still selected (and therefore still present).
  const imageCache = {};

  function widgetRoot() {
    return document.querySelector('.rebuy-widget.widget-type-dynamic-bundle');
  }

  function pidClassOf(el) {
    return Array.from(el.classList).find(c => c.startsWith('product-id-'));
  }

  // Read whatever images are currently in the native strip and remember them.
  function cacheStripImages(root) {
    root.querySelectorAll('.rebuy-bundle__images .rebuy-bundle__image').forEach(cell => {
      const pid = pidClassOf(cell);
      if (!pid) return;
      const img = cell.querySelector('img');
      const src = img && img.getAttribute('src');
      if (!src) return;
      imageCache[pid] = { src: src, alt: (img.getAttribute('alt') || '') };
    });
  }

  // Ensure every product block has our persistent thumbnail.
  function injectThumbs(root) {
    root.querySelectorAll('.rebuy-bundle__items .rebuy-product-block').forEach(block => {
      const info = block.querySelector('.rebuy-product-info');
      if (!info) return;
      // Idempotent — never add a second thumbnail.
      if (info.querySelector('.rebuy-cloned-thumb-wrapper')) return;

      const pid = pidClassOf(block);
      const data = pid && imageCache[pid];
      if (!data) return;

      const img = document.createElement('img');
      img.className = 'rebuy-cloned-thumb';
      img.src = data.src;
      img.alt = data.alt;
      img.loading = 'lazy';

      const wrapper = document.createElement('div');
      wrapper.className = 'rebuy-cloned-thumb-wrapper';
      wrapper.appendChild(img);

      info.insertBefore(wrapper, info.firstChild);
    });
  }

  // The "This item" product (first block) must stay selected — the customer is
  // on its PDP, so the bundle makes no sense without it. Rebuy lets it be
  // unchecked like any other product, so we re-assert its checked state here and
  // block the toggle click in init() below.
  function firstBlock(root) {
    return root.querySelector('.rebuy-bundle__items .rebuy-product-block');
  }

  function lockFirstItem(root) {
    const block = firstBlock(root);
    if (!block) return;
    block.classList.add('rebuy-locked-item');
    const checkbox = block.querySelector('.rebuy-product-checkbox input[type="checkbox"]');
    // Re-check if Rebuy (or a stray click) left it unchecked after a re-render.
    if (checkbox && !checkbox.checked) checkbox.checked = true;
  }

  function enhance() {
    const root = widgetRoot();
    if (!root) return;
    cacheStripImages(root);
    injectThumbs(root);
    lockFirstItem(root);
  }

  // Debounce bursts of mutations into a single enhance() per frame.
  let scheduled = false;
  function scheduleEnhance() {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(() => {
      scheduled = false;
      enhance();
    });
  }

  // Stop any attempt to uncheck the first ("This item") product. We listen on
  // the document in the capture phase so we intercept the click before Rebuy's
  // own handlers run — and because Rebuy re-renders the widget on every toggle,
  // a single delegated listener survives those re-renders (re-binding per node
  // would not). The other products toggle normally.
  function blockFirstItemUncheck() {
    document.addEventListener('click', function (e) {
      const checkbox = e.target.closest && e.target.closest('.rebuy-product-checkbox');
      if (!checkbox) return;

      const root = widgetRoot();
      if (!root || !root.contains(checkbox)) return;

      const block = checkbox.closest('.rebuy-product-block');
      if (block && block === firstBlock(root)) {
        e.preventDefault();
        e.stopImmediatePropagation();
        const input = checkbox.querySelector('input[type="checkbox"]');
        if (input) input.checked = true;
      }
    }, true);
  }

  function init() {
    enhance(); // in case the widget is already mounted
    blockFirstItemUncheck();

    // Rebuy mounts late and re-renders on every checkbox toggle, so keep watching.
    const container = document.querySelector('[data-rebuy-id]') || document.body;
    new MutationObserver(scheduleEnhance).observe(container, {
      childList: true,
      subtree: true
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
